import { useState } from "react";

export const faqs = [
  {
    q: "How fast can I get a passport in Lakewood Ranch, FL?",
    a: "Our fastest service processes passports in as little as 24–72 hours. Rush processing takes 4–7 business days, and standard expedited service takes 2–3 weeks. The speed you choose depends on your travel date and urgency — whether it is a school trip to London in three weeks, a golf pilgrimage to Scotland in ten days, or an emergency flight to Argentina for polo season.",
  },
  {
    q: "Where is the closest passport acceptance facility to Lakewood Ranch?",
    a: "The closest facility is USPS Lakewood Ranch at 11135 State Road 70 E. Other nearby options include USPS Bradenton Main (~20–25 min west via SR-70), USPS Sarasota Main (~20–25 min south via I-75), USPS North Port (~35–40 min south via I-75), and Manatee County Clerk in downtown Bradenton (~20–25 min west). All require appointments for passport services.",
  },
  {
    q: "Do I need a passport to cruise from Tampa if I live in Lakewood Ranch?",
    a: "Yes. All Caribbean and international cruises from Port Tampa Bay require a valid U.S. passport for every passenger, including children. Even closed-loop cruises that start and end at the same U.S. port strongly recommend carrying a passport in case you need to fly home from a foreign port due to illness, injury, or emergency.",
  },
  {
    q: "Can I renew my passport by mail, or do I need to visit an acceptance facility?",
    a: "If your most recent passport was issued when you were 16 or older, is undamaged, was issued within the last 15 years, and was in your current name (or you have legal documentation of a name change), you can renew by mail using Form DS-82 — no acceptance facility visit needed. Otherwise, you need Form DS-11 and an in-person visit.",
  },
  {
    q: "What is the child passport trap?",
    a: "Child passports (for applicants under 16) are only valid for 5 years. Many parents forget this shorter validity period and discover their child's passport has expired right before a school trip or family vacation. The child must also appear in person with both parents (or proper consent documentation) for every application.",
  },
  {
    q: "Do I need six months of passport validity for golf trips to Scotland and Ireland?",
    a: "Yes. The United Kingdom and Ireland require your passport to be valid for at least six months beyond your return date. Do not assume your passport is fine just because it has not expired — check the specific validity requirements for every destination on your itinerary, including connecting countries.",
  },
  {
    q: "Can Fast Passport Center take my passport photo?",
    a: "We provide detailed guidance on State Department photo requirements and can direct you to approved photo partners in Lakewood Ranch, Bradenton, and Sarasota — including locations on State Road 70, in downtown Sarasota, and near University Parkway. The photo must be 2×2 inches, taken against a white or off-white background, with a neutral expression and no glasses.",
  },
  {
    q: "What if my passport was damaged by humidity in my Lakewood Ranch home?",
    a: "Humidity damage is common in Florida's Gulf Coast climate. Water damage, salt air exposure, mold, separating bindings, or any physical compromise invalidates a passport. If your passport shows any damage from Florida's climate — even if the data page is still readable — it must be replaced immediately using Form DS-11 with an acceptance facility visit.",
  },
  {
    q: "I have a non-refundable school trip to Europe. How do I make sure my child's passport is valid?",
    a: "School-organized European trips typically require passports valid for at least six months beyond the return date, and many require passport copies 3–4 months before departure for group bookings and travel insurance. Check your child's passport expiration as soon as the trip is announced — child passports are only valid for 5 years, and many expire unnoticed during the school year.",
  },
  {
    q: "What forms of payment do you accept?",
    a: "We accept all major credit cards, debit cards, and bank transfers. Government fees are paid separately via check or money order made payable to the U.S. Department of State, as required by federal regulations.",
  },
  {
    q: "Do you offer any guarantees?",
    a: "We guarantee that your application will be submitted correctly and tracked throughout processing. If a delay occurs due to an error we should have caught, we work to resolve it at no additional charge. Travel is inherently unpredictable, but our track record with Southwest Florida clients — including Lakewood Ranch families, golfers, polo families, and school trip organizers — speaks for itself.",
  },
  {
    q: "How do I get my documents to you from Lakewood Ranch?",
    a: "We arrange secure document pickup through our courier network or provide prepaid overnight shipping labels for Sarasota and Manatee County residents. Your documents are insured, tracked, and handled by bonded couriers from pickup through delivery of your completed passport — whether you are at your Country Club East home, your condo near Main Street, or preparing for a polo tournament at the Sarasota Polo Club.",
  },
];

export default function LakewoodRanchFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-16 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Frequently Asked Questions
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Passport Questions — Lakewood Ranch, FL
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            Quick answers to the most common questions from Lakewood Ranch and Sarasota / Manatee County travelers.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`border rounded-lg transition-all ${openIndex === i ? "border-[#F6C344] bg-[#FFF8E7]/30" : "border-gray-200 bg-white hover:border-gray-300"}`}
            >
              <button
                className="w-full flex items-start justify-between gap-4 p-5 text-left"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="font-semibold text-[#1A365D] text-sm">{faq.q}</span>
                <i
                  className={`ri-arrow-down-s-line text-[#F6C344] text-lg shrink-0 transition-transform ${openIndex === i ? "rotate-180" : ""}`}
                ></i>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}